import Store from "./Store";
import { Geometry } from "geojson";
import { computed } from "mobx";

export default class Base {
  store: Store;

  id: string;
  name: string;
  type: string;
  //totals
  totals: number[] = [];
  //totals per 100k inhabitants
  totals100k: number[] = [];
  //total increase in percentage of previous day
  totalsP: number[] = [];

  //total per square km
  totalskm2: number[] = [];

  //increase
  deltas: number[] = [];
  //increase per 100k inhabitants
  deltas100k: number[] = [];
  //increase in percentage of previous day
  deltasP: number[] = [];

  deltaskm2: number[] = [];

  lastTotal: number = 0;
  lastTotal100k: number = 0;
  lastTotalP: number = 0;
  lastTotalkm2: number = 0;

  lastDelta: number = 0;
  lastDelta100k: number = 0;
  lastDeltaP: number = 0;
  lastDeltakm2: number = 0;

  maxTotal: number = 0;
  maxTotal100k: number = 0;
  maxTotalP: number = 0;
  maxTotalkm2: number = 0;

  maxDelta: number = 0;
  maxDelta100k: number = 0;
  maxDeltaP: number = 0;
  maxDeltakm2: number = 0;

  inhabitants: number = 0;
  surface: number = 0;
  geometry: Geometry;

  constructor(store: Store) {
    this.store = store;
  }

  @computed get total() {
    return this.totals[this.store.activeIndex];
  }

  @computed get total100k() {
    return this.totals100k[this.store.activeIndex];
  }

  @computed get totalP() {
    return this.totalsP[this.store.activeIndex];
  }

  @computed get delta() {
    return this.deltas[this.store.activeIndex];
  }

  @computed get delta100k() {
    return this.deltas100k[this.store.activeIndex];
  }

  @computed get deltaP() {
    return this.deltasP[this.store.activeIndex];
  }

  @computed get count() {
    if (this.store.asIncrement) {
      return this.store.as100k ? this.delta100k : this.delta;
    }
    return this.store.as100k ? this.total100k : this.total;
  }

  @computed get minMax() {
    let min = Infinity;
    let max = 0;
    let trg;
    if (this.store.asIncrement) {
      trg = this.store.as100k ? this.deltas100k : this.deltas;
    } else {
      trg = this.store.as100k ? this.totals100k : this.totals;
    }

    trg.forEach(d => {
      if (d < min) min = d;
      if (d > max) max = d;
    });

    if (max === Infinity) {
      return { min: 0, max: 0 };
    }

    return { min: Math.max(min, 0), max };
  }

  @computed get feature() {
    return {
      type: "Feature",
      id: this.id,
      properties: {
        id: this.id,
        type: this.type,
        name: this.name,
        total: this.total,
        total100k: this.total100k,
        delta: this.delta,
        delta100k: this.delta100k
      },
      geometry: this.geometry
    };
  }

  @computed get geoJSON() {
    return {
      type: "FeatureCollection",
      name: this.name,
      crs: {
        type: "name",
        properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" }
      },
      features: [this.feature]
    };
  }

  calc(d: number[]) {
    this.totals = d;
    this.maxTotal = maxValue(this.totals);
    this.totals100k = this.totals.map(v => {
      if (this.inhabitants === 0) return 0;
      let b = v / this.inhabitants;
      let result = Math.floor(b * 100000);
      if (result > this.maxTotal100k) this.maxTotal100k = result;
      return result;
    });

    this.totalsP = this.totals.map((t, n) => {
      if (n > 0) {
        let p = this.totals[n - 1];
        if (p === 0) return 0;
        let v = t / p - 1;
        let result = Math.round(v * 100);
        if (result > this.maxTotalP) this.maxTotalP = result;
        return result;
      }
      return 0;
    });

    let km2 = this.surface / 100000000;
    this.totalskm2 = this.totals.map(v => {
      if (this.surface === 0) return 0;
      let b = v / km2;
      let result = Math.round(b);
      if (result > this.maxTotalkm2) this.maxTotal100k = result;
      return result;
    });

    this.deltas = calcDelta(d);

    this.deltas100k = this.deltas.map(v => {
      if (this.inhabitants === 0) return 0;
      let b = v / this.inhabitants;
      let result = Math.floor(b * 100000);
      if (result > this.maxDelta100k) this.maxDelta100k = result;
      return result;
    });

    this.deltasP = this.deltas.map((t, n) => {
      if (n > 0) {
        let p = this.deltas[n - 1];
        if (p === 0) return 0;
        let v = t / p - 1;
        let result = Math.round(v * 100);
        if (result > this.maxDeltaP) this.maxDeltaP = result;
        return result;
      }
      return 0;
    });

    this.lastTotal = this.totals[this.totals.length - 1];
    this.lastTotal100k = this.totals100k[this.totals100k.length - 1];
    this.lastTotalP = this.totalsP[this.totalsP.length - 1];
    this.lastTotalkm2 = this.totalskm2[this.totalskm2.length - 1];

    this.lastDelta = this.deltas[this.deltas.length - 1];
    this.lastDelta100k = this.deltas100k[this.deltas100k.length - 1];
    this.lastDeltaP = this.deltasP[this.deltasP.length - 1];
    this.lastDeltakm2 = this.deltaskm2[this.deltaskm2.length - 1];
  }
}

function calcDelta(d: number[]) {
  let result = [...d];
  for (let i = d.length - 1; i >= 0; i--) {
    if (i - 1 >= 0) {
      result[i] = d[i] - d[i - 1];
    } else {
      result[i] = 0;
    }
  }
  return result;
}

function maxValue(d: number[]) {
  return d.reduce((acc, cur) => {
    if (cur > acc) return cur;
    return acc;
  }, 0);
}
